import { useEffect, useCallback } from "react";

import {
  connectTicketMessagesSocket,
  disconnectTicketMessagesSocket,
  getTicketMessagesSocket,
} from "src/Services/socket/ticketMessagesSocket";

/**
 * Custom hook that provides real-time updates for the TicketList page.
 *
 * When a ticket message notification arrives via WebSocket, this hook triggers
 * a refresh callback so the ticket list and its per-ticket unread indicators
 * stay up to date without manual page reload.
 *
 * IMPORTANT: This hook does NOT manage the "ticket-notifications" room subscription.
 * The room subscription is owned exclusively by useTicketUnreadIndicator (mounted
 * in the sidemenu, which is always visible). This hook only attaches/detaches its
 * own event listener on the shared socket. This design prevents the room subscription
 * from being dropped when the TicketList unmounts (e.g. user navigates to TicketDetail).
 *
 * It also listens for browser visibilitychange events to refresh the list when
 * the user returns to the tab after it was hidden.
 *
 * @param {Object} options
 * @param {Function} options.onRefresh - Callback to reload tickets and unread IDs.
 *   Called when a real-time notification arrives or the tab regains focus.
 */
const useTicketListRealtime = ({ onRefresh }) => {
  const stableRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  useEffect(() => {
    const socket = connectTicketMessagesSocket();

    /**
     * Handles "ticket-new-message-notification" events from the global room.
     * Only triggers a refresh if the tab is currently visible (avoids redundant
     * fetches while the tab is hidden — a full refresh happens on tab re-focus).
     */
    const handleNotification = (payload) => {
      if (!payload?.ticketId) return;
      if (document.visibilityState === "visible") {
        stableRefresh();
      }
    };

    /** Refreshes the list when the user switches back to this browser tab. */
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      stableRefresh();
    };

    socket.on("ticket-new-message-notification", handleNotification);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      const s = getTicketMessagesSocket();
      s.off("ticket-new-message-notification", handleNotification);
      disconnectTicketMessagesSocket();
    };
  }, [stableRefresh]);
};

export default useTicketListRealtime;
